import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../hooks/useTranslate';
import { useNavigate } from 'react-router-dom';
import { databricksService } from '../services/databricksService';
import { dataObjectService } from '../services/dataObjectService';
import { reportService } from '../services/reportService';
import { dataDescriptionService, DataDescriptionAnalysis } from '../services/dataDescriptionService';

import { useToast } from '../hooks/useToast';
import { useAsyncJob } from '../hooks/useAsyncJob';
import { useLanguage } from '../contexts/LanguageContext';
import InlineFileUploadPanel from './InlineFileUploadPanel';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import DataObjectConfig from './modal/DataObjectConfig';
import { DatabricksCatalog, DatabricksSchema, DatabricksTableMeta } from '../types/databricks';
import { Folder, FileColumn } from '../types';
import { getCachedData, setCachedData, clearDatabricksCache, CACHE_KEYS } from '../utils/databricksCache';
import TableAccessRequestButton from './modal/TableAccessRequestButton';
import { RiInformationFill } from 'react-icons/ri';
import ContextualHelpLink from './training/ContextualHelpLink';

interface AddDataSourceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadFile: (file: File, dataObjectData?: { name: string; description?: string; columns?: any[] }) => Promise<{ jobId?: string; dataObjectName?: string } | void> | void;
    onDatabricksAnalysisComplete?: (dataSource: { id: number; name: string; uuid?: string }, columns?: FileColumn[], tableName?: string) => void;
    title?: string;
    pendingUploadFolder?: Folder | null;
}

const AddDataSourceModal: React.FC<AddDataSourceModalProps> = ({
    isOpen,
    onClose,
    onUploadFile,
    onDatabricksAnalysisComplete,
    title = "Add Data Object",
    pendingUploadFolder
}) => {
    const { push: pushToast } = useToast();
    const navigate = useNavigate();
    const { t } = useTranslate();
    const { activeLanguage } = useLanguage();
    const [activeTab, setActiveTab] = useState<'upload' | 'databricks'>('upload');
    // Upload tab state lifted from inline panel
    const [uploadState, setUploadState] = useState<{ selectedFile: File | null; analyzing: boolean; uploading: boolean; canSubmit: boolean; handleSubmit: () => Promise<void> | void; handleReset: () => void; } | null>(null);

    // Databricks selection state
    const [catalogs, setCatalogs] = useState<DatabricksCatalog[]>([]);
    const [schemas, setSchemas] = useState<DatabricksSchema[]>([]);
    const [tables, setTables] = useState<DatabricksTableMeta[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<string>('');
    const [selectedSchema, setSelectedSchema] = useState<string>('');
    const [selectedTable, setSelectedTable] = useState<string>('');
    const [loadingCatalogs, setLoadingCatalogs] = useState(false);
    const [loadingSchemas, setLoadingSchemas] = useState(false);
    const [loadingTables, setLoadingTables] = useState(false);
    const [databricksError, setDatabricksError] = useState<string | null>(null);
    const [addingTable, setAddingTable] = useState(false);

    // Search state for dropdowns
    const [catalogSearch, setCatalogSearch] = useState<string>('');
    const [schemaSearch, setSchemaSearch] = useState<string>('');
    const [tableSearch, setTableSearch] = useState<string>('');
    const [showCatalogDropdown, setShowCatalogDropdown] = useState(false);
    const [showSchemaDropdown, setShowSchemaDropdown] = useState(false);
    const [showTableDropdown, setShowTableDropdown] = useState(false);

    // Keyboard navigation state
    const [catalogFocusedIndex, setCatalogFocusedIndex] = useState(-1);
    const [schemaFocusedIndex, setSchemaFocusedIndex] = useState(-1);
    const [tableFocusedIndex, setTableFocusedIndex] = useState(-1);

    // Refs for dropdown click-outside detection
    const catalogRef = useRef<HTMLDivElement>(null);
    const schemaRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    // Refs for dropdown containers to enable auto-scroll
    const catalogDropdownRef = useRef<HTMLDivElement>(null);
    const schemaDropdownRef = useRef<HTMLDivElement>(null);
    const tableDropdownRef = useRef<HTMLDivElement>(null);

    // Refs for input fields to enable auto-focus
    const catalogInputRef = useRef<HTMLInputElement>(null);
    const schemaInputRef = useRef<HTMLInputElement>(null);
    const tableInputRef = useRef<HTMLInputElement>(null);

    const [analyzingDatabricks, setAnalyzingDatabricks] = useState(false);
    const [tableAnalysis, setTableAnalysis] = useState<DataDescriptionAnalysis | null>(null);
    const [showTableConfig, setShowTableConfig] = useState(false); // Like showColumnTable in upload

    // Cancel confirmation state
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    // Table configuration state
    const [tableObjectName, setTableObjectName] = useState('');
    const [tableObjectDescription, setTableObjectDescription] = useState('');
    const [tableNameError, setTableNameError] = useState('');
    const [selectedColumnsForReport, setSelectedColumnsForReport] = useState<string[]>([]);
    const [currentDataObjectName, setCurrentDataObjectName] = useState<string>('');
    const [currentDataObjectUuid, setCurrentDataObjectUuid] = useState<string | null>(null);
    const tableNameInputRef = React.useRef<HTMLInputElement | null>(null);

    // Auto-focus tracking for schema input after catalog selection
    const [shouldAutoFocusSchema, setShouldAutoFocusSchema] = useState(false);

    // Animation states
    const [isClosing, setIsClosing] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Tab switching animation states
    const [isTabSwitching, setIsTabSwitching] = useState(false);
    const [previousTab, setPreviousTab] = useState<'upload' | 'databricks'>('upload');
    const tableDescriptionRef = React.useRef<HTMLTextAreaElement | null>(null);
    const tableLastFocusedRef = React.useRef<HTMLElement | null>(null);
    const catalogsLoadedRef = React.useRef(false);

    // Async job management
    const asyncJob = useAsyncJob({
        onJobComplete: async (status) => {
            if (status.status === 'SUCCESS') {
                // Reset adding state before closing
                setAddingTable(false);

                // Close modal first
                onClose();

                // Show success toast
                pushToast({
                    variant: 'success',
                    message: `Analysis completed for ${currentDataObjectName}! Redirecting to report...`
                });

                // Navigate to report
                if (status.historical_report_id) {
                    navigate(`/report?reportId=${status.historical_report_id}&dataObjectName=${encodeURIComponent(currentDataObjectName)}`);
                } else if (currentDataObjectUuid) {
                    // Fallback to live analysis if no historical report ID
                    navigate('/report', {
                        state: {
                            dataObjectName: currentDataObjectName,
                            dataObjectUuid: currentDataObjectUuid,
                        }
                    });
                } else {
                    // Final fallback without UUID
                    navigate('/report', {
                        state: {
                            dataObjectName: currentDataObjectName
                        }
                    });
                }
            } else if (status.status === 'FAILED') {
                // Reset adding state on failure
                setAddingTable(false);

                pushToast({
                    variant: 'error',
                    message: `Analysis failed for ${currentDataObjectName}. ${status.error_message || 'Please try again.'}`
                });
                // Close modal on failure too
                onClose();
            }
        },
        onJobUpdate: (status) => {
            // Job status updates handled by asyncJob hook
        },
    });

    // Callback for upload state changes with useCallback to prevent re-renders
    const handleUploadStateChange = React.useCallback((s: any) => {
        setUploadState(s);

        // Start async job when we have jobId and upload is complete
        if (s?.jobId && s?.dataObjectName && s?.uploadComplete) {
            setCurrentDataObjectName(s.dataObjectName);
            setCurrentDataObjectUuid(s.dataObjectUuid || null);
            asyncJob.startPolling(s.jobId);
        }
    }, [asyncJob]);

    // Filtered data based on search terms
    const filteredCatalogs = useMemo(() => {
        if (!catalogSearch) return catalogs;
        return catalogs.filter(c => c.name.toLowerCase().includes(catalogSearch.toLowerCase()));
    }, [catalogs, catalogSearch]);

    const filteredSchemas = useMemo(() => {
        if (!schemaSearch) return schemas;
        return schemas.filter(s => s.name.toLowerCase().includes(schemaSearch.toLowerCase()));
    }, [schemas, schemaSearch]);

    const filteredTables = useMemo(() => {
        if (!tableSearch) return tables;
        return tables.filter(t => t.name.toLowerCase().includes(tableSearch.toLowerCase()));
    }, [tables, tableSearch]);

    // Click outside handler to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (catalogRef.current && !catalogRef.current.contains(event.target as Node)) {
                setShowCatalogDropdown(false);
                setCatalogFocusedIndex(-1);
            }
            if (schemaRef.current && !schemaRef.current.contains(event.target as Node)) {
                setShowSchemaDropdown(false);
                setSchemaFocusedIndex(-1);
            }
            if (tableRef.current && !tableRef.current.contains(event.target as Node)) {
                setShowTableDropdown(false);
                setTableFocusedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            // Store current scroll position
            const scrollY = window.scrollY;
            // Prevent body scroll
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';

            return () => {
                // Restore body scroll and position
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                // Restore scroll position
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);

    // Auto-focus schema input after catalog selection and schemas load
    useEffect(() => {
        if (shouldAutoFocusSchema && schemas.length > 0 && !loadingSchemas) {
            setShouldAutoFocusSchema(false);
            setTimeout(() => {
                catalogInputRef.current?.blur();
                setShowSchemaDropdown(true);
                setSchemaFocusedIndex(-1);
                setTimeout(() => {
                    schemaInputRef.current?.focus();
                }, 50);
            }, 100);
        }
    }, [shouldAutoFocusSchema, schemas.length, loadingSchemas]);

    // Allow editing column descriptions/types for delta table prior to adding
    const updateTableColumn = useCallback((columnName: string, key: string, value: any) => {
        setTableAnalysis(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                columns: prev.columns.map(c => c.name === columnName ? { ...c, [key]: value } : c)
            };
        });
    }, []);

    const validateTableMeta = useCallback(() => {
        if (!tableObjectName.trim()) {
            setTableNameError(t('Data object name is required'));
            return false;
        }
        setTableNameError('');
        return true;
    }, [tableObjectName]);

    // Memoized validation result to prevent infinite re-renders
    const isTableMetaValid = useMemo(() => {
        return tableObjectName.trim().length > 0;
    }, [tableObjectName]);

    const handleAnalyzeTable = useCallback(async () => {
        if (analyzingDatabricks) return;
        setDatabricksError(null); // Clear any previous errors
        setAnalyzingDatabricks(true);
        try {
            // Construct the full table name in catalog.schema.table format
            const fullTableName = `${selectedCatalog}.${selectedSchema}.${selectedTable}`;

            // Use data-description API to analyze the table
            const analysis = await dataDescriptionService.analyzeTable(fullTableName, activeLanguage);

            const enriched: DataDescriptionAnalysis = {
                ...analysis,
                columns: analysis.columns.map(c => ({
                    ...c,
                    detectedType: c.dataType,
                    selectedType: c.dataType
                }))
            };

            // Set all state atomically to prevent transition gaps
            setTableAnalysis(enriched);
            setTableObjectName(selectedTable);
            setTableObjectDescription(analysis.description);
            setShowTableConfig(true); // This must be last to trigger Priority 3
        } catch (error) {
            let errorMessage = t('Failed to analyze table');
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            setDatabricksError(errorMessage);
            // Reset analysis state but keep table selection for retry
            setTableAnalysis(null);
            setShowTableConfig(false);
            // DON'T reset selectedTable - let user try again with same table
            setTableObjectName('');
            setTableObjectDescription('');
            setTableNameError('');
        } finally {
            setAnalyzingDatabricks(false);
        }
    }, [analyzingDatabricks, selectedTable]);

    const handleTryAgainPriority2 = useCallback(() => {
        setDatabricksError(null);
        setTableAnalysis(null);
        setShowTableConfig(false);
        setAnalyzingDatabricks(false);
    }, []);

    const handleTryAgainAnalysis = useCallback(() => {
        setDatabricksError(null);
        // Trigger analysis again
        document.querySelector<HTMLElement>('[data-analyze-table]')?.click();
    }, []);

    // Fetch table size when table is selected
    const handleSelectDifferentTable = useCallback(() => {
        setDatabricksError(null);
        setSelectedTable('');
        setSelectedSchema('');
        setSelectedCatalog('');
    }, []);

    const handleBackToTableSelection = useCallback(() => {
        // Re-selecting table...
        // Reset all table analysis state atomically
        setShowTableConfig(false);
        setTableAnalysis(null);
        setTableObjectName('');
        setTableObjectDescription('');
        setTableNameError('');
        setSelectedTable('');
        setAnalyzingDatabricks(false);
    }, []);

    // Reset all Databricks state
    const resetDatabricksState = useCallback(() => {
        // Clear selections
        setSelectedCatalog('');
        setSelectedSchema('');
        setSelectedTable('');

        // Clear search terms
        setCatalogSearch('');
        setSchemaSearch('');
        setTableSearch('');

        // Close dropdowns
        setShowCatalogDropdown(false);
        setShowSchemaDropdown(false);
        setShowTableDropdown(false);

        // Reset focus indices
        setCatalogFocusedIndex(-1);
        setSchemaFocusedIndex(-1);
        setTableFocusedIndex(-1);

        // Clear data arrays
        setSchemas([]);
        setTables([]);

        // Clear table analysis state
        setShowTableConfig(false);
        setTableAnalysis(null);
        setTableObjectName('');
        setTableObjectDescription('');
        setTableNameError('');
        setAnalyzingDatabricks(false);
        setAddingTable(false);

        // Clear errors
        setDatabricksError(null);
    }, []);



    // Auto-scroll for catalog dropdown
    useEffect(() => {
        if (catalogDropdownRef.current && catalogFocusedIndex >= 0) {
            const dropdown = catalogDropdownRef.current;
            const focusedElement = dropdown.children[catalogFocusedIndex] as HTMLElement;
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [catalogFocusedIndex]);

    // Auto-scroll for schema dropdown
    useEffect(() => {
        if (schemaDropdownRef.current && schemaFocusedIndex >= 0) {
            const dropdown = schemaDropdownRef.current;
            const focusedElement = dropdown.children[schemaFocusedIndex] as HTMLElement;
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [schemaFocusedIndex]);

    // Auto-scroll for table dropdown
    useEffect(() => {
        if (tableDropdownRef.current && tableFocusedIndex >= 0) {
            const dropdown = tableDropdownRef.current;
            const focusedElement = dropdown.children[tableFocusedIndex] as HTMLElement;
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [tableFocusedIndex]);

    // Reset Databricks state when modal opens
    useEffect(() => {
        if (isOpen) {
            resetDatabricksState();
        }
    }, [isOpen, resetDatabricksState]);

    // Reset Databricks state when modal opens
    useEffect(() => {
        if (isOpen) {
            resetDatabricksState();
        }
    }, [isOpen, resetDatabricksState]);

    // Load catalogs when tab opens
    useEffect(() => {
        if (activeTab === 'databricks' && catalogs.length === 0 && !loadingCatalogs && !catalogsLoadedRef.current) {
            catalogsLoadedRef.current = true;

            // Try to load from cache first
            const cachedCatalogs = getCachedData<DatabricksCatalog[]>(CACHE_KEYS.CATALOGS);
            if (cachedCatalogs) {

                setCatalogs(cachedCatalogs);
                return;
            }

            setLoadingCatalogs(true);
            setDatabricksError(null);
            databricksService.listCatalogs()
                .then(data => {
                    setCatalogs(data);
                    setCachedData(CACHE_KEYS.CATALOGS, data);
                })
                .catch(() => {
                    setDatabricksError(t('Failed to load catalogs'));
                    catalogsLoadedRef.current = false; // Reset on error to allow retry
                    clearDatabricksCache(); // Clear potentially stale cache
                })
                .finally(() => setLoadingCatalogs(false));
        }
    }, [activeTab]); // Only depend on activeTab to avoid infinite loops

    const onCatalogChange = useCallback((value: string) => {
        // This function is called by dropdown clicks, don't modify search states here
        if (value) {
            // Try to load schemas from cache first
            const cachedSchemas = getCachedData<DatabricksSchema[]>(CACHE_KEYS.SCHEMAS, value);
            if (cachedSchemas) {
                setSchemas(cachedSchemas);
                return;
            }

            setLoadingSchemas(true);
            setDatabricksError(null);
            databricksService.listSchemas(value)
                .then(data => {
                    setSchemas(data);
                    setCachedData(CACHE_KEYS.SCHEMAS, data, value);
                })
                .catch(() => {
                    setDatabricksError(t('Failed to load schemas'));
                    // Clear schema cache for this catalog
                    sessionStorage.removeItem(CACHE_KEYS.SCHEMAS);
                })
                .finally(() => setLoadingSchemas(false));
        }
    }, []);  // No dependencies needed as it only calls setters

    const onSchemaChange = useCallback((value: string) => {
        // This function is called by dropdown clicks, don't modify search states here
        if (value && selectedCatalog) {
            const tableKey = `${selectedCatalog}.${value}`;
            // Try to load tables from cache first
            const cachedTables = getCachedData<DatabricksTableMeta[]>(CACHE_KEYS.TABLES, tableKey);
            if (cachedTables) {
                setTables(cachedTables);
                return;
            }

            setLoadingTables(true);
            setDatabricksError(null);
            databricksService.listTables(selectedCatalog, value)
                .then(data => {
                    setTables(data);
                    setCachedData(CACHE_KEYS.TABLES, data, tableKey);
                })
                .catch(() => {
                    setDatabricksError(t('Failed to load tables'));
                    // Clear table cache for this schema
                    sessionStorage.removeItem(CACHE_KEYS.TABLES);
                })
                .finally(() => setLoadingTables(false));
        }
    }, [selectedCatalog]);  // Depends on selectedCatalog

    // Animated tab switching
    const switchToTab = (newTab: 'upload' | 'databricks') => {
        if (newTab === activeTab || isTabSwitching) return;

        setPreviousTab(activeTab);
        setIsTabSwitching(true);

        // Start fade out
        setTimeout(() => {
            setActiveTab(newTab);
        }, 150);

        // Complete fade in
        setTimeout(() => {
            setIsTabSwitching(false);
        }, 300);
    };

    const onTableChange = useCallback((value: string) => {
        // This function is called by dropdown clicks, search states already updated
        // Just store the selected table value
    }, []);  // No dependencies needed

    // Check if there's work in progress that might be lost
    const hasWorkInProgress = useCallback(() => {
        // File upload states - ALWAYS show confirmation for file upload work
        const fileUploadInProgress = uploadState?.analyzing ||
            uploadState?.selectedFile ||
            uploadState?.uploading;

        // Databricks states - only show confirmation during initial setup, not when async job is running
        const databricksInProgress = (analyzingDatabricks || addingTable) && !asyncJob.isRunning;

        return fileUploadInProgress || databricksInProgress;
    }, [uploadState, analyzingDatabricks, addingTable, asyncJob.isRunning]);

    const performClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setActiveTab('upload');
            setIsTabSwitching(false);
            setPreviousTab('upload');

            // Reset all Databricks state
            resetDatabricksState();

            setShowCancelConfirmation(false);
            catalogsLoadedRef.current = false;
            setMounted(false);
            onClose();
        }, 1300); // 1200ms animation + 100ms buffer
    }, [onClose]);

    const handleClose = useCallback(() => {
        // If any async job is running (file upload or databricks), close immediately and refresh to show processing card
        if (asyncJob.isRunning) {
            // Async job running - closing and refreshing
            onClose();
            window.location.reload();
            return;
        }

        // For all other cases (file upload setup or databricks setup), show confirmation if there's work in progress
        if (hasWorkInProgress()) {
            setShowCancelConfirmation(true);
            return;
        }

        // Proceed with normal close
        performClose();
    }, [asyncJob.isRunning, hasWorkInProgress, performClose, onClose]);

    const handleCancelConfirm = () => {
        // If any async job is running, refresh page to show processing card
        if (asyncJob.isRunning) {
            // Cancelling with async job - refreshing page
            onClose();
            window.location.reload();
        } else {
            // For regular work in progress (setup/configuration), just close normally
            performClose();
        }
    };

    const handleCancelDeny = () => {
        setShowCancelConfirmation(false);
    };

    // Handle mount/unmount animations
    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
            setMounted(true);
        }
    }, [isOpen]);

    const handleAddDataObject = useCallback(async () => {
        if (!selectedTable || addingTable || !isTableMetaValid) return;
        if (!validateTableMeta()) return; // Still run validation to set error message
        setDatabricksError(null);
        setAddingTable(true);
        try {
            // Construct the full table name in catalog.schema.table format
            const fullTableName = `${selectedCatalog}.${selectedSchema}.${selectedTable}`;

            const dataSource = await dataObjectService.createDeltaTableDataSource(
                fullTableName,
                (tableAnalysis!.columns || []).map(c => ({
                    name: c.name,
                    dataType: (c as any).selectedType || c.dataType,
                    description: c.description || '',
                    sampleValues: c.sampleValues || [], // Include sample values from analysis
                    analysis_flag: selectedColumnsForReport.length > 0
                        ? selectedColumnsForReport.includes(c.name)
                        : true
                })),
                tableObjectDescription || tableAnalysis!.description || `Delta table ${fullTableName}`,
                tableObjectName.trim(),
                pendingUploadFolder || undefined
            );

            // Start async analysis job and show progress inline
            try {
                // Extract folder information for analysis
                const folderInfo = pendingUploadFolder ? {
                    folderId: pendingUploadFolder.id,
                    folderUuid: pendingUploadFolder.uuid,
                    folderName: pendingUploadFolder.name
                } : undefined;

                // Backend retrieves description from data_object_id
                const { jobId } = await reportService.startAsyncAnalysisTable(
                    fullTableName,
                    dataSource.name,
                    dataSource.uuid,
                    folderInfo,
                    activeLanguage
                );


                // Update data object status to analyzing
                await dataObjectService.updateDataSource(dataSource.id || 0, {
                    status: 'analyzing',
                    dataObjectUuid: dataSource.uuid
                });

                // Start async analysis tracking for Databricks
                setCurrentDataObjectName(dataSource.name || tableObjectName);
                setCurrentDataObjectUuid(dataSource.uuid);
                asyncJob.startPolling(jobId);

                // Call the completion callback to handle navigation/closing
                if (onDatabricksAnalysisComplete) {
                    // Pass the configured columns (which may contain DQ rules) to the completion handler
                    // Note: columns may have been enhanced with additional properties by updateTableColumn
                    const columnsWithRules = (tableAnalysis?.columns || []).map(c => {
                        const enhancedColumn = c as any; // Type assertion to access dynamic properties
                        return {
                            name: c.name,
                            detectedType: enhancedColumn.detectedType || c.dataType || 'string',
                            description: c.description || '',
                            rules: enhancedColumn.rules || '' // DQ rules configured by user via updateTableColumn
                        };
                    });

                    await onDatabricksAnalysisComplete(dataSource, columnsWithRules, selectedTable);
                }

                // Don't set addingTable to false - the async job state will handle the UI transition
                // This prevents the brief flash of table configuration UI
            } catch (analysisError) {
                console.error('[AddDataSourceModal] Analysis failed:', analysisError);
                setAddingTable(false); // Reset adding state on analysis failure
                // Note: Analysis error toast handled by polling completion to avoid duplicates
                handleClose();
            }
        } catch (e) {
            console.error('[AddDataSourceModal] Error in Add Data Object:', e);
            setAddingTable(false); // Reset adding state on general failure
            setDatabricksError(e instanceof Error ? e.message : t('Failed to create data object'));
        }
    }, [selectedTable, addingTable, isTableMetaValid, validateTableMeta, tableObjectName, tableObjectDescription, tableAnalysis, pushToast, handleClose, onDatabricksAnalysisComplete]);

    if (!isOpen && !mounted) return null;

    return createPortal(
        <>
            <div
                className={`modal-backdrop transition-all duration-[1200ms] ease-in-out ${isClosing ? 'bg-black/0' : ''
                    }`}
                onClick={() => { }} // Disabled backdrop click to prevent accidental closes
            >
                <div
                    className={`modal-content modal-size-xl transition-all duration-[1200ms] ease-in-out ${isClosing
                        ? 'scale-[0.08] opacity-0 transform translate-x-[75vw] translate-y-[-55vh] rotate-[18deg]'
                        : 'scale-100 opacity-100 transform translate-x-0 translate-y-0 rotate-0'
                        }`}
                    style={{
                        transformOrigin: isClosing ? 'top right' : 'center',
                        height: '90vh',
                        maxHeight: '90vh'
                    }}
                >
                    <div className="modal-header">
                        <div />
                        <h2 className="modal-title">{title}</h2>
                        <div className="flex items-center gap-2">
                            <ContextualHelpLink videoId="tv-002" />
                            <button onClick={handleClose} className="modal-close" aria-label="Close dialog">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="modal-tabs">
                        <button
                            type="button"
                            onClick={() => switchToTab('upload')}
                            disabled={isTabSwitching}
                            className={`tab-button transition-all duration-200 ${activeTab === 'upload' ? 'tab-button--active' : ''} ${isTabSwitching ? 'opacity-70' : 'hover:opacity-80'
                                }`}
                        >{t('Upload File')}</button>
                        <button
                            type="button"
                            onClick={() => switchToTab('databricks')}
                            disabled={isTabSwitching}
                            className={`tab-button transition-all duration-200 ${activeTab === 'databricks' ? 'tab-button--active' : ''} ${isTabSwitching ? 'opacity-70' : 'hover:opacity-80'
                                }`}
                        >{t('Databricks Delta Table')}</button>
                    </div>

                    <div className="modal-body dd-modal-body" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '80vh',
                        height: '80vh',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        {/* Generating Report Overlay - show only for sync uploading, not for async analysis */}
                        {activeTab === 'upload' && uploadState?.uploading && !asyncJob.isRunning && (
                            <div className="doctor-loading-overlay">
                                <div className="doctor-logo">
                                    <span style={{ color: 'red', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem' }}>{t('Generating Report')}</span>
                                    <div className="heartbeat-loader" style={{ maxWidth: '400px', height: '120px' }}>
                                        <svg viewBox="0 0 550 206" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 100 L80 100 L90 60 L100 140 L110 40 L120 160 L130 100 L200 100 L210 80 L220 120 L230 70 L240 130 L250 100 L320 100 L330 65 L340 135 L350 50 L360 150 L370 100 L440 100 L450 75 L460 125 L470 55 L480 145 L490 100 L540 100"
                                                fill="none"
                                                stroke="red"
                                                strokeWidth="6"
                                                strokeLinecap="round"
                                                strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="progress-dots" style={{ display: 'none' }}>
                                    <div className="dot"></div>
                                    <div className="dot"></div>
                                    <div className="dot"></div>
                                </div>
                            </div>
                        )}
                        {/* Loading Overlay for Report Generation - only for databricks sync operations */}
                        {activeTab === 'databricks' && addingTable && !asyncJob.isRunning && !asyncJob.jobStatus && (
                            <div className="doctor-loading-overlay">
                                <div className="doctor-logo">
                                    <span style={{ color: 'red', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem' }}>{t('Generating Report')}</span>
                                    <div className="heartbeat-loader" style={{ maxWidth: '400px', height: '120px' }}>
                                        <svg viewBox="0 0 550 206" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 100 L80 100 L90 60 L100 140 L110 40 L120 160 L130 100 L200 100 L210 80 L220 120 L230 70 L240 130 L250 100 L320 100 L330 65 L340 135 L350 50 L360 150 L370 100 L440 100 L450 75 L460 125 L470 55 L480 145 L490 100 L540 100"
                                                fill="none"
                                                stroke="red"
                                                strokeWidth="6"
                                                strokeLinecap="round"
                                                strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="progress-dots" style={{ display: 'none' }}>
                                    <div className="dot"></div>
                                    <div className="dot"></div>
                                    <div className="dot"></div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'upload' && (
                            <div
                                className={`upload-mode-content transition-all duration-300 ease-in-out ${isTabSwitching
                                    ? 'opacity-0 transform translate-x-[-20px]'
                                    : 'opacity-100 transform translate-x-0'
                                    }`}
                                style={{
                                    flex: '1 1 auto',
                                    overflow: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: '100%'
                                }}>
                                {/* Show progress inline during analysis */}
                                {asyncJob.isRunning || asyncJob.jobStatus ? (
                                    <div className="flex-1 flex items-center justify-center p-8">
                                        <div className="text-center space-y-6 max-w-md w-full">
                                            <div className="text-center">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                                    {t('Analyzing')} {currentDataObjectName}
                                                </h3>
                                                <p className="text-sm text-gray-600 mb-6">
                                                    {t('Please wait while we analyze your data...')}
                                                </p>
                                            </div>

                                            {/* Enhanced Progress Bar with Red Theme */}
                                            <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
                                                <div
                                                    className="bg-gradient-to-r from-red-500 to-red-600 h-4 rounded-full transition-all duration-500 ease-out shadow-lg relative overflow-hidden"
                                                    style={{ width: `${asyncJob.progress || 0}%` }}
                                                >
                                                    {/* Animated shine effect */}
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                                                </div>
                                            </div>

                                            <div className="text-center text-sm text-gray-600">
                                                <span className="notranslate" translate="no">{asyncJob.progress || 0}%</span> {t('complete')}
                                            </div>

                                            {/* Show error if failed */}
                                            {asyncJob.isFailed && asyncJob.error && (
                                                <div className="text-center text-sm text-red-600 mt-2">
                                                    {asyncJob.error}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="upload-area-container" style={{
                                        flex: '1 1 auto',
                                        overflow: 'hidden'
                                    }}>
                                        <InlineFileUploadPanel
                                            onUpload={onUploadFile}
                                            constrainHeight={true}
                                            suppressInternalFooter
                                            onStateChange={handleUploadStateChange}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'databricks' && (
                            <div style={{ flex: '1 1 auto', overflow: 'auto', height: '100%' }}>
                                {/* Show async job progress when report generation is running */}
                                {asyncJob.isRunning || asyncJob.jobStatus ? (
                                    <div className="h-full w-full flex items-center justify-center" style={{ minHeight: '500px' }}>
                                        <div className="text-center space-y-6 max-w-md w-full">
                                            <div className="text-center">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                                    {t('Analyzing')} {currentDataObjectName}
                                                </h3>
                                                <p className="text-sm text-gray-600 mb-6">
                                                    {t('Please wait while we analyze your data...')}
                                                </p>
                                            </div>

                                            {/* Enhanced Progress Bar with Red Theme */}
                                            <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
                                                <div
                                                    className="bg-gradient-to-r from-red-500 to-red-600 h-4 rounded-full transition-all duration-500 ease-out shadow-lg relative overflow-hidden"
                                                    style={{ width: `${asyncJob.progress || 0}%` }}
                                                >
                                                    {/* Animated shine effect */}
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                                                </div>
                                            </div>

                                            <div className="text-center text-sm text-gray-600">
                                                <span className="notranslate" translate="no">{asyncJob.progress || 0}%</span> {t('complete')}
                                            </div>

                                            {/* Show error if failed */}
                                            {asyncJob.isFailed && asyncJob.error && (
                                                <div className="text-center text-sm text-red-600 mt-2">
                                                    {asyncJob.error}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : analyzingDatabricks ? (
                                    <div className="upload-area-container" style={{
                                        flex: '1 1 auto',
                                        overflow: 'hidden',
                                        padding: '0 2rem'
                                    }}>
                                        <div className="dd-loading-container flex flex-col items-center justify-center min-h-96" role="status" aria-live="polite">
                                            <span style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem' }}>{t('Analyzing Metadata')}</span>
                                            <div className="heartbeat-loader" style={{ maxWidth: '400px', height: '120px' }}>
                                                <svg viewBox="0 0 550 206" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M10 100 L80 100 L90 60 L100 140 L110 40 L120 160 L130 100 L200 100 L210 80 L220 120 L230 70 L240 130 L250 100 L320 100 L330 65 L340 135 L350 50 L360 150 L370 100 L440 100 L450 75 L460 125 L470 55 L480 145 L490 100 L540 100"
                                                        fill="none"
                                                        stroke="white"
                                                        strokeWidth="6"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                ) : showTableConfig && tableAnalysis ? (
                                    <div className="upload-area-container" style={{
                                        flex: '1 1 auto',
                                        overflow: 'auto',
                                        maxHeight: '100%'
                                    }}>
                                        <DataObjectConfig
                                            dataObjectName={tableObjectName}
                                            dataObjectDescription={tableObjectDescription}
                                            nameError={tableNameError}
                                            onNameChange={(v) => { setTableObjectName(v); if (tableNameError && v.trim()) setTableNameError(''); }}
                                            onDescriptionChange={setTableObjectDescription}
                                            nameInputRef={tableNameInputRef}
                                            descriptionRef={tableDescriptionRef}
                                            lastUserFocusedRef={tableLastFocusedRef}
                                            analysis={({
                                                ...tableAnalysis,
                                                columns: tableAnalysis.columns.map(c => ({
                                                    ...c,
                                                    detectedType: (c as any).detectedType || c.dataType,
                                                    selectedType: (c as any).selectedType || c.dataType
                                                })),
                                                allDqRules: tableAnalysis.allDqRules || []
                                            }) as any}
                                            onColumnUpdate={updateTableColumn}
                                            dataObjectId={currentDataObjectUuid || undefined}
                                            tableName={selectedTable}
                                            isValid={isTableMetaValid}
                                            isProcessing={addingTable}
                                            onSubmit={handleAddDataObject}
                                            submitLabel={t('Generate Report')}
                                            onReset={undefined}
                                            resetLabel={t('Reset')}
                                            showFooter={false}
                                            onSelectionChange={setSelectedColumnsForReport}
                                            initialSelectedColumns={tableAnalysis?.columns?.map(c => c.name) || []}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex flex-col h-full overflow-auto">
                                        {/* Table selection area */}
                                        <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-6 shadow-sm mx-2 sm:mx-8 mt-2 sm:mt-4 flex-shrink-0">
                                            <div className="mb-3 sm:mb-6">
                                                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">{t('Select Delta Table')}</h3>
                                                <p className="text-xs sm:text-sm text-gray-600">{t('Choose a Databricks Delta table to analyze by selecting the catalog, schema, and table.')}</p>
                                                {databricksError && (
                                                    <div className="mt-2 sm:mt-4 p-2 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                                                        <div className="flex items-center">
                                                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mr-1.5 sm:mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                            </svg>
                                                            <p className="text-xs sm:text-sm text-red-700">{databricksError}</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-4">
                                                {/* Catalog Selection */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('Catalog')}</label>
                                                    <div className="relative" ref={catalogRef}>
                                                        <input
                                                            ref={catalogInputRef}
                                                            type="text"
                                                            value={catalogSearch || (selectedCatalog ? `✓ ${selectedCatalog}` : '')}
                                                            onChange={(e) => {
                                                                if (selectedCatalog && e.target.value.startsWith('✓')) {
                                                                    // Clear selection if user starts typing over selected value
                                                                    setSelectedCatalog('');
                                                                    setCatalogSearch(e.target.value.replace(/^✓\s.*/, ''));
                                                                } else {
                                                                    setCatalogSearch(e.target.value);
                                                                }
                                                                setShowCatalogDropdown(true);
                                                            }}
                                                            placeholder={selectedCatalog ? "" : t('Search catalogs...')}
                                                            disabled={loadingCatalogs}
                                                            className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 cursor-pointer text-sm bg-white appearance-none ${selectedCatalog ? 'text-red-600 font-semibold' : 'text-gray-900'
                                                                }`}
                                                            onFocus={() => setShowCatalogDropdown(true)}
                                                            onClick={() => {
                                                                if (!showCatalogDropdown) {
                                                                    setShowCatalogDropdown(true);
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                // Route keyboard navigation to the appropriate active dropdown
                                                                let eventHandled = false;

                                                                // If table dropdown is open, handle table navigation
                                                                if (showTableDropdown) {
                                                                    const filteredTables = tables.filter(table => table.name.toLowerCase().includes(tableSearch.toLowerCase()));

                                                                    if (e.key === 'ArrowDown') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        setTableFocusedIndex(prev => {
                                                                            const newIndex = prev < filteredTables.length - 1 ? prev + 1 : 0;
                                                                            setTimeout(() => {
                                                                                const dropdown = tableDropdownRef.current;
                                                                                const focusedElement = dropdown?.querySelector(`[data-index="${newIndex}"]`);
                                                                                if (focusedElement) {
                                                                                    focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                                }
                                                                            }, 10);
                                                                            return newIndex;
                                                                        });
                                                                    } else if (e.key === 'ArrowUp') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        setTableFocusedIndex(prev => {
                                                                            const newIndex = prev > 0 ? prev - 1 : filteredTables.length - 1;
                                                                            setTimeout(() => {
                                                                                const dropdown = tableDropdownRef.current;
                                                                                const focusedElement = dropdown?.querySelector(`[data-index="${newIndex}"]`);
                                                                                if (focusedElement) {
                                                                                    focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                                }
                                                                            }, 10);
                                                                            return newIndex;
                                                                        });
                                                                    } else if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        if (tableFocusedIndex >= 0 && tableFocusedIndex < filteredTables.length) {
                                                                            const selectedTab = filteredTables[tableFocusedIndex];
                                                                            setSelectedTable(selectedTab.name);
                                                                            setTableSearch('');
                                                                            setShowTableDropdown(false);
                                                                            setTableFocusedIndex(-1);
                                                                            onTableChange(selectedTab.name);
                                                                        }
                                                                    } else if (e.key === 'Escape') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        setShowTableDropdown(false);
                                                                        setTableFocusedIndex(-1);
                                                                    }
                                                                }

                                                                // If schema dropdown is open, handle schema navigation
                                                                if (showSchemaDropdown && !eventHandled) {
                                                                    const filteredSchemas = schemas.filter(schema => schema.name.toLowerCase().includes(schemaSearch.toLowerCase()));

                                                                    if (e.key === 'ArrowDown') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        setSchemaFocusedIndex(prev => {
                                                                            const newIndex = prev < filteredSchemas.length - 1 ? prev + 1 : 0;
                                                                            setTimeout(() => {
                                                                                const dropdown = schemaDropdownRef.current;
                                                                                const focusedElement = dropdown?.querySelector(`[data-index="${newIndex}"]`);
                                                                                if (focusedElement) {
                                                                                    focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                                }
                                                                            }, 10);
                                                                            return newIndex;
                                                                        });
                                                                    } else if (e.key === 'ArrowUp') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        setSchemaFocusedIndex(prev => {
                                                                            const newIndex = prev > 0 ? prev - 1 : filteredSchemas.length - 1;
                                                                            setTimeout(() => {
                                                                                const dropdown = schemaDropdownRef.current;
                                                                                const focusedElement = dropdown?.querySelector(`[data-index="${newIndex}"]`);
                                                                                if (focusedElement) {
                                                                                    focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                                }
                                                                            }, 10);
                                                                            return newIndex;
                                                                        });
                                                                    } else if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        if (schemaFocusedIndex >= 0 && schemaFocusedIndex < filteredSchemas.length) {
                                                                            const selectedSch = filteredSchemas[schemaFocusedIndex];
                                                                            setSelectedSchema(selectedSch.name);
                                                                            setSchemaSearch('');
                                                                            setShowSchemaDropdown(false);
                                                                            setSchemaFocusedIndex(-1);
                                                                            onSchemaChange(selectedSch.name);
                                                                            setTimeout(() => {
                                                                                setShowTableDropdown(true);
                                                                                setTableFocusedIndex(-1);
                                                                            }, 150);
                                                                        }
                                                                    } else if (e.key === 'Escape') {
                                                                        e.preventDefault();
                                                                        eventHandled = true;
                                                                        setShowSchemaDropdown(false);
                                                                        setSchemaFocusedIndex(-1);
                                                                    }
                                                                }

                                                                // Handle catalog dropdown navigation (only if no other dropdown handled the event)
                                                                if (!eventHandled) {
                                                                    const filteredCatalogs = catalogs.filter(catalog => catalog.name.toLowerCase().includes(catalogSearch.toLowerCase()));

                                                                    if (e.key === 'ArrowDown') {
                                                                        e.preventDefault();
                                                                        setShowCatalogDropdown(true);
                                                                        setCatalogFocusedIndex(prev => {
                                                                            if (!showCatalogDropdown) {
                                                                                return 0;
                                                                            }
                                                                            const newIndex = prev < filteredCatalogs.length - 1 ? prev + 1 : 0;
                                                                            setTimeout(() => {
                                                                                const dropdown = catalogDropdownRef.current;
                                                                                const focusedElement = dropdown?.querySelector(`[data-index="${newIndex}"]`);
                                                                                if (focusedElement) {
                                                                                    focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                                }
                                                                            }, 10);
                                                                            return newIndex;
                                                                        });
                                                                    } else if (e.key === 'ArrowUp') {
                                                                        e.preventDefault();
                                                                        setShowCatalogDropdown(true);
                                                                        setCatalogFocusedIndex(prev => {
                                                                            if (!showCatalogDropdown) {
                                                                                return filteredCatalogs.length - 1;
                                                                            }
                                                                            const newIndex = prev > 0 ? prev - 1 : filteredCatalogs.length - 1;
                                                                            setTimeout(() => {
                                                                                const dropdown = catalogDropdownRef.current;
                                                                                const focusedElement = dropdown?.querySelector(`[data-index="${newIndex}"]`);
                                                                                if (focusedElement) {
                                                                                    focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                                }
                                                                            }, 10);
                                                                            return newIndex;
                                                                        });
                                                                    } else if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        if (showCatalogDropdown && catalogFocusedIndex >= 0 && catalogFocusedIndex < filteredCatalogs.length) {
                                                                            const selectedCat = filteredCatalogs[catalogFocusedIndex];
                                                                            setSelectedCatalog(selectedCat.name);
                                                                            setCatalogSearch('');
                                                                            setShowCatalogDropdown(false);
                                                                            setCatalogFocusedIndex(-1);
                                                                            onCatalogChange(selectedCat.name);
                                                                            setTimeout(() => {
                                                                                setShowSchemaDropdown(true);
                                                                                setSchemaFocusedIndex(-1);
                                                                            }, 150);
                                                                        }
                                                                    } else if (e.key === 'Escape') {
                                                                        setShowCatalogDropdown(false);
                                                                        setCatalogFocusedIndex(-1);
                                                                        catalogInputRef.current?.blur();
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                            {loadingCatalogs ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                            ) : (
                                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        {showCatalogDropdown && !loadingCatalogs && catalogs.length > 0 && (
                                                            <div ref={catalogDropdownRef} className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                                                {catalogs
                                                                    .filter(catalog => catalog.name.toLowerCase().includes(catalogSearch.toLowerCase()))
                                                                    .map((catalog, index) => (
                                                                        <div
                                                                            key={catalog.name}
                                                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${selectedCatalog === catalog.name
                                                                                ? 'bg-red-50 text-red-800 font-semibold border-l-2 border-red-500'
                                                                                : catalogFocusedIndex === index
                                                                                    ? 'bg-blue-50 text-blue-800 border-l-2 border-blue-500'
                                                                                    : 'text-gray-700'
                                                                                }`}
                                                                            onMouseDown={() => {
                                                                                setSelectedCatalog(catalog.name);
                                                                                setCatalogSearch('');
                                                                                setShowCatalogDropdown(false);
                                                                                onCatalogChange(catalog.name);
                                                                                // Auto-open schema dropdown after catalog selection
                                                                                setTimeout(() => {
                                                                                    setShowSchemaDropdown(true);
                                                                                    schemaInputRef.current?.focus();
                                                                                }, 100);
                                                                            }}
                                                                        >
                                                                            {catalog.name}
                                                                        </div>
                                                                    )
                                                                    )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Schema Selection */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('Schema')}</label>
                                                    <div className="relative" ref={schemaRef}>
                                                        <input
                                                            ref={schemaInputRef}
                                                            type="text"
                                                            value={schemaSearch || (selectedSchema ? `✓ ${selectedSchema}` : '')}
                                                            onChange={(e) => {
                                                                if (selectedSchema && e.target.value.startsWith('✓')) {
                                                                    // Clear selection if user starts typing over selected value
                                                                    setSelectedSchema('');
                                                                    setSchemaSearch(e.target.value.replace(/^✓\s.*/, ''));
                                                                } else {
                                                                    setSchemaSearch(e.target.value);
                                                                }
                                                                if (selectedCatalog) setShowSchemaDropdown(true);
                                                            }}
                                                            placeholder={selectedSchema ? "" : (!selectedCatalog ? t('Select catalog first') : t('Search schemas...'))}
                                                            disabled={!selectedCatalog || loadingSchemas}
                                                            className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 cursor-pointer text-sm bg-white appearance-none ${selectedSchema ? 'text-red-600 font-semibold' : 'text-gray-900'
                                                                }`}
                                                            onFocus={() => selectedCatalog && setShowSchemaDropdown(true)}
                                                            onClick={() => {
                                                                if (selectedCatalog && !showSchemaDropdown) {
                                                                    setShowSchemaDropdown(true);
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                // Only handle keyboard events if schema dropdown is open OR no other dropdowns are open
                                                                if (showCatalogDropdown || showTableDropdown) {
                                                                    return;
                                                                }

                                                                const filteredSchemas = schemas.filter(schema => schema.name.toLowerCase().includes(schemaSearch.toLowerCase()));

                                                                if (e.key === 'ArrowDown') {
                                                                    e.preventDefault();
                                                                    if (selectedCatalog) {
                                                                        setShowSchemaDropdown(true);
                                                                        setSchemaFocusedIndex(prev => {
                                                                            if (!showSchemaDropdown) {
                                                                                // If dropdown was closed, start at first item
                                                                                return 0;
                                                                            }
                                                                            // If dropdown was open, move to next item
                                                                            return prev < filteredSchemas.length - 1 ? prev + 1 : 0;
                                                                        });
                                                                    }
                                                                } else if (e.key === 'ArrowUp') {
                                                                    e.preventDefault();
                                                                    if (selectedCatalog) {
                                                                        setShowSchemaDropdown(true);
                                                                        setSchemaFocusedIndex(prev => {
                                                                            if (!showSchemaDropdown) {
                                                                                // If dropdown was closed, start at last item
                                                                                return filteredSchemas.length - 1;
                                                                            }
                                                                            // If dropdown was open, move to previous item
                                                                            return prev > 0 ? prev - 1 : filteredSchemas.length - 1;
                                                                        });
                                                                    }
                                                                } else if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    if (showSchemaDropdown && schemaFocusedIndex >= 0 && schemaFocusedIndex < filteredSchemas.length) {
                                                                        const selectedSch = filteredSchemas[schemaFocusedIndex];
                                                                        setSelectedSchema(selectedSch.name);
                                                                        setSchemaSearch('');
                                                                        setShowSchemaDropdown(false);
                                                                        setSchemaFocusedIndex(-1);
                                                                        onSchemaChange(selectedSch.name);
                                                                        setTimeout(() => {
                                                                            setShowTableDropdown(true);
                                                                            setTableFocusedIndex(-1);
                                                                            tableInputRef.current?.focus();
                                                                        }, 150);
                                                                    }
                                                                } else if (e.key === 'Escape') {
                                                                    setShowSchemaDropdown(false);
                                                                    setSchemaFocusedIndex(-1);
                                                                    schemaInputRef.current?.blur();
                                                                }
                                                            }}
                                                        />
                                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                            {loadingSchemas ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                            ) : (
                                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        {showSchemaDropdown && !loadingSchemas && schemas.length > 0 && (
                                                            <div ref={schemaDropdownRef} className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                                                {schemas
                                                                    .filter(schema => schema.name.toLowerCase().includes(schemaSearch.toLowerCase()))
                                                                    .map((schema, index) => (
                                                                        <div
                                                                            key={schema.name}
                                                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${selectedSchema === schema.name
                                                                                ? 'bg-red-50 text-red-800 font-semibold border-l-2 border-red-500'
                                                                                : schemaFocusedIndex === index
                                                                                    ? 'bg-blue-50 text-blue-800 border-l-2 border-blue-500'
                                                                                    : 'text-gray-700'
                                                                                }`}
                                                                            onMouseDown={() => {
                                                                                setSelectedSchema(schema.name);
                                                                                setSchemaSearch('');
                                                                                setShowSchemaDropdown(false);
                                                                                onSchemaChange(schema.name);
                                                                                // Auto-open table dropdown after schema selection
                                                                                setTimeout(() => {
                                                                                    setShowTableDropdown(true);
                                                                                    tableInputRef.current?.focus();
                                                                                }, 100);
                                                                            }}
                                                                        >
                                                                            {schema.name}
                                                                        </div>
                                                                    )
                                                                    )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Table Selection */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('Table')}</label>
                                                    <div className="relative" ref={tableRef}>
                                                        <input
                                                            ref={tableInputRef}
                                                            type="text"
                                                            value={tableSearch || (selectedTable ? `✓ ${selectedTable}` : '')}
                                                            onChange={(e) => {
                                                                if (selectedTable && e.target.value.startsWith('✓')) {
                                                                    // Clear selection if user starts typing over selected value
                                                                    setSelectedTable('');
                                                                    setTableSearch(e.target.value.replace(/^✓\s.*/, ''));
                                                                } else {
                                                                    setTableSearch(e.target.value);
                                                                }
                                                                if (selectedSchema) setShowTableDropdown(true);
                                                            }}
                                                            placeholder={selectedTable ? "" : (!selectedSchema ? t('Select schema first') : t('Search tables...'))}
                                                            disabled={!selectedSchema || loadingTables}
                                                            className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 cursor-pointer text-sm bg-white appearance-none ${selectedTable ? 'text-red-600 font-semibold' : 'text-gray-900'
                                                                }`}
                                                            onFocus={() => {
                                                                if (selectedSchema) {
                                                                    setShowTableDropdown(true);
                                                                    setTableFocusedIndex(-1);
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                if (selectedSchema && !showTableDropdown) {
                                                                    setShowTableDropdown(true);
                                                                }
                                                            }}
                                                        />
                                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                            {loadingTables ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                            ) : (
                                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        {showTableDropdown && !loadingTables && tables.length > 0 && (
                                                            <div ref={tableDropdownRef} className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                                                {tables
                                                                    .filter(table => table.name.toLowerCase().includes(tableSearch.toLowerCase()))
                                                                    .map((table, index) => (
                                                                        <div
                                                                            key={table.name}
                                                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${selectedTable === table.name
                                                                                ? 'bg-red-50 text-red-800 font-semibold border-l-2 border-red-500'
                                                                                : tableFocusedIndex === index
                                                                                    ? 'bg-blue-50 text-blue-800 border-l-2 border-blue-500'
                                                                                    : 'text-gray-700'
                                                                                }`}
                                                                            onMouseDown={() => {
                                                                                setSelectedTable(table.name);
                                                                                setTableSearch('');
                                                                                setShowTableDropdown(false);
                                                                                onTableChange(table.name);
                                                                            }}
                                                                        >
                                                                            {table.name}
                                                                        </div>
                                                                    )
                                                                    )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Selected table summary */}
                                            {selectedTable && (
                                                <div className="mt-3 sm:mt-6 p-2 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                                                    <h4 className="text-xs sm:text-sm font-semibold text-red-800 mb-1.5 sm:mb-3">✓ {t('Selected Table')}</h4>
                                                    <div className="space-y-1 sm:space-y-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-gray-600 w-14 sm:w-16">{t('Catalog:')}</span>
                                                            <span className="text-xs sm:text-sm font-mono text-red-700 bg-white/60 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-red-200 truncate">
                                                                {selectedCatalog}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-gray-600 w-14 sm:w-16">{t('Schema:')}</span>
                                                            <span className="text-xs sm:text-sm font-mono text-red-700 bg-white/60 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-red-200 truncate">
                                                                {selectedSchema}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-gray-600 w-14 sm:w-16">{t('Table:')}</span>
                                                            <span className="text-xs sm:text-sm font-mono text-red-700 bg-white/60 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-red-200 truncate">
                                                                {selectedTable}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Can't find tables? Request access section - Only show during table selection, not during analysis or configuration */}
                    {activeTab === 'databricks' && !showTableConfig && !analyzingDatabricks && (
                        <div className="px-3 sm:px-6 pb-2 sm:pb-4 flex justify-end">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-red-700/20 rounded-full blur-sm transition-all duration-300 group-hover:blur"></div>
                                <div className="relative flex items-center gap-1.5 sm:gap-3 bg-gradient-to-r from-red-50/90 to-red-100/90 backdrop-blur-sm border border-red-300/60 rounded-full pl-2 sm:pl-4 pr-1.5 sm:pr-2 py-1.5 sm:py-2 shadow-lg hover:shadow-xl transition-all duration-300">
                                    <p className="text-red-800 text-[10px] sm:text-xs font-semibold whitespace-nowrap">
                                        {t("Can't find your tables? Request access")}
                                    </p>
                                    <TableAccessRequestButton
                                        catalog={selectedCatalog || ''}
                                        schema={selectedSchema || ''}
                                        className="text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full"
                                        onRequestSubmitted={(success, message) => {
                                            // Here you would show a toast notification
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="modal-footer" style={{ flexShrink: 0 }}>
                        <div className="layout-cluster justify-end gap-fluid-sm">
                            {activeTab === 'upload' && (
                                <div className="layout-cluster gap-fluid-sm">
                                    {/* Show close button during async analysis, otherwise show regular buttons */}
                                    {asyncJob.isRunning || asyncJob.jobStatus ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                asyncJob.stopPolling();
                                                handleClose();
                                            }}
                                            className="btn btn-secondary"
                                        >
                                            {t('Close')}
                                        </button>
                                    ) : uploadState ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={uploadState.handleReset}
                                                disabled={uploadState.analyzing || uploadState.uploading}
                                                className="btn btn-secondary"
                                            >
                                                {t('Reset')}
                                            </button>
                                            {uploadState.canSubmit && (
                                                <button
                                                    type="button"
                                                    onClick={uploadState.handleSubmit}
                                                    disabled={uploadState.analyzing || uploadState.uploading || asyncJob.isRunning}
                                                    className="btn btn-primary"
                                                >
                                                    {asyncJob.isRunning ? t('Analyzing...') : uploadState.analyzing ? t('Analyzing...') : uploadState.uploading ? t('Uploading...') : t('Generate Report')}
                                                </button>
                                            )}
                                        </>
                                    ) : null}
                                </div>
                            )}
                            {/* Databricks tab footer */}
                            {activeTab === 'databricks' && (
                                <div className="layout-cluster gap-fluid-sm">
                                    {/* Show close button during async analysis */}
                                    {asyncJob.isRunning || asyncJob.jobStatus ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                asyncJob.stopPolling();
                                                handleClose();
                                            }}
                                            className="btn btn-secondary"
                                        >
                                            {t('Close')}
                                        </button>
                                    ) : showTableConfig ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={handleBackToTableSelection}
                                                disabled={analyzingDatabricks || addingTable}
                                                className="btn btn-secondary"
                                            >
                                                {t('Reset')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleAddDataObject}
                                                disabled={!isTableMetaValid || analyzingDatabricks || addingTable}
                                                className="btn btn-primary"
                                            >
                                                {analyzingDatabricks ? t('Analyzing...') : addingTable ? t('Generating...') : t('Generate Report')}
                                            </button>
                                        </>
                                    ) : analyzingDatabricks ? (
                                        <button
                                            type="button"
                                            disabled={true}
                                            className="btn btn-primary"
                                        >
                                            {t('Analyzing...')}
                                        </button>
                                    ) : selectedTable ? (
                                        /* When table is selected, show analyze button in footer */
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedCatalog('');
                                                    setSelectedSchema('');
                                                    setSelectedTable('');
                                                    resetDatabricksState();
                                                }}
                                                className="btn btn-secondary"
                                            >
                                                {t('Reset Selection')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleAnalyzeTable}
                                                disabled={analyzingDatabricks}
                                                className="btn btn-primary"
                                            >
                                                {analyzingDatabricks ? t('Analyzing...') : t('Analyze Table')}
                                            </button>
                                        </>
                                    ) : (
                                        /* When no table selected, show reset button only */
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedCatalog('');
                                                setSelectedSchema('');
                                                setSelectedTable('');
                                                setCatalogSearch('');
                                                setSchemaSearch('');
                                                setTableSearch('');
                                                setShowCatalogDropdown(false);
                                                setShowSchemaDropdown(false);
                                                setShowTableDropdown(false);
                                                setDatabricksError(null);
                                            }}
                                            className="btn btn-secondary"
                                        >
                                            {t('Reset')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Cancel Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={showCancelConfirmation}
                onClose={handleCancelDeny}
                onConfirm={handleCancelConfirm}
                entityName={t('your work')}
                title={t('Cancel Upload?')}
                confirmLabel={t('Yes, Cancel')}
                description={t('Are you sure you want to cancel? You may lose your current progress including file selection, analysis, and any configuration changes.')}
                secondaryLabel={t('No, Keep Working')}
                onSecondary={handleCancelDeny}
            />
        </>,
        document.body
    );
};

export default AddDataSourceModal;
